import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";
import DisclaimerBanner from "../components/DisclaimerBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AlertTriangle, Clock, Trash2, Undo2, HardDrive, Timer } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Account() {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/account/deletion-status"),
        api.get("/account/retention"),
      ]);
      setStatus(s.data);
      setRetention(r.data);
    } catch (e) {
      toast.error("Could not load account status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const request = async () => {
    if (!confirmEmail.trim()) return toast.error("Please type your email to confirm");
    if (!window.confirm("Are you sure? This begins a 30-day recovery window and will delete your account data permanently unless canceled.")) return;
    setSubmitting(true);
    try {
      await api.post("/account/deletion-request", { confirm_email: confirmEmail.trim() });
      toast.success("Deletion scheduled. You have 30 days to cancel from this page.");
      setConfirmEmail("");
      await load();
      await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule deletion");
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async () => {
    setSubmitting(true);
    try {
      await api.post("/account/deletion-request/cancel");
      toast.success("Deletion canceled");
      await load();
      await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <DisclaimerBanner />
      <AppHeader />
      <main className="max-w-[860px] mx-auto px-6 py-12" data-testid="account-page">
        <div className="label-tiny mb-3">Account</div>
        <h1 className="font-serif-display text-4xl md:text-5xl font-light text-slate-900 leading-tight">
          Your <em className="not-italic font-semibold">data</em>.
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Signed in as <span className="font-medium">{user?.email}</span>. You are in control of your data.
        </p>

        <section className="mt-10 border border-slate-200" data-testid="retention-card">
          <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-slate-700" />
            <h2 className="font-serif-display text-2xl text-slate-900">Storage &amp; retention</h2>
          </div>
          <div className="p-6 space-y-5">
            <div className="border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 flex items-start gap-2">
              <Timer className="w-4 h-4 mt-0.5 shrink-0 text-slate-600" />
              <div>
                <span className="font-medium text-slate-900">Research &amp; Development phase.</span>{" "}
                Free workspaces include up to {retention?.storage_quota_mb ?? 100} MB of file storage.
                Workspaces and uploaded files may be removed after {retention?.inactivity_limit_days ?? 30} days of
                inactivity, with advance notice. You can download your materials at any time from the Preview &amp; Export tab of each matter.
              </div>
            </div>

            {retention && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                <RetentionStat
                  label="Storage used"
                  value={`${retention.storage_used_mb ?? 0} MB`}
                  hint={`of ${retention.storage_quota_mb} MB`}
                  bar={{ pct: Math.min(100, ((retention.storage_used_mb || 0) / retention.storage_quota_mb) * 100), tone: (retention.storage_used_mb || 0) / retention.storage_quota_mb > 0.9 ? "red" : "orange" }}
                  testid="retention-storage"
                />
                <RetentionStat
                  label="Idle days"
                  value={`${retention.idle_days ?? 0}`}
                  hint={`limit ${retention.inactivity_limit_days} days`}
                  bar={{ pct: Math.min(100, ((retention.idle_days || 0) / retention.inactivity_limit_days) * 100), tone: retention.idle_days >= 28 ? "red" : retention.idle_days >= 21 ? "amber" : "slate" }}
                  testid="retention-idle"
                />
                <RetentionStat
                  label="Removal in"
                  value={`${retention.days_until_removal ?? retention.inactivity_limit_days} days`}
                  hint={retention.last_active_at ? `last active ${new Date(retention.last_active_at).toLocaleDateString()}` : ""}
                  bar={null}
                  testid="retention-removal"
                />
              </div>
            )}

            {retention?.reminder_tier && (
              <div
                className={`border p-3 text-xs flex items-start gap-2 ${
                  retention.reminder_tier >= 28
                    ? "border-red-300 bg-red-50 text-red-900"
                    : "border-amber-300 bg-amber-50 text-amber-900"
                }`}
                data-testid={`retention-notice-day-${retention.reminder_tier}`}
              >
                <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${retention.reminder_tier >= 28 ? "text-red-700" : "text-amber-700"}`} />
                <div>
                  <span className="font-medium">
                    {retention.reminder_tier >= 28 ? "Day 28 reminder" : "Day 21 reminder"}:
                  </span>{" "}
                  This workspace will be removed in {retention.days_until_removal} day
                  {retention.days_until_removal === 1 ? "" : "s"} unless you sign in or open a matter before then.
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="mt-10 border border-slate-200" data-testid="delete-my-data-card">
          <div className="border-b border-slate-200 px-6 py-4 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <h2 className="font-serif-display text-2xl text-slate-900">Delete my data</h2>
          </div>
          <div className="p-6 space-y-5">
            <div className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
              <div>
                Requesting deletion schedules a <strong>soft delete with a 30-day recovery window</strong>. All of your
                cases, evidence uploads, and generated drafts will be preserved and inaccessible during that time. If you
                do not cancel within 30 days, everything is permanently erased.
              </div>
            </div>

            {loading ? (
              <div className="label-tiny">Loading…</div>
            ) : status?.pending ? (
              <div className="space-y-4" data-testid="deletion-pending">
                <div className="border border-red-300 bg-red-50 p-4 text-sm text-red-900 flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 shrink-0 text-red-700" />
                  <div>
                    <div className="font-medium">Deletion pending</div>
                    <div className="mt-1 text-red-800">
                      Requested {new Date(status.requested_at).toLocaleString()} · Permanent erasure scheduled for{" "}
                      <strong>{new Date(status.scheduled_at).toLocaleDateString()}</strong> ({status.days_remaining} days remaining).
                    </div>
                  </div>
                </div>
                <Button onClick={cancel} disabled={submitting} data-testid="cancel-deletion-btn"
                  className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">
                  <Undo2 className="w-4 h-4 mr-2" /> {submitting ? "Canceling…" : "Cancel deletion"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-700">
                  To confirm, type your account email address <span className="font-mono-tech text-slate-900">{user?.email}</span> below:
                </p>
                <Input
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={user?.email || "your email"}
                  className="rounded-none border-slate-300"
                  data-testid="deletion-confirm-email"
                />
                <Button onClick={request} disabled={submitting || !confirmEmail.trim()}
                  data-testid="request-deletion-btn"
                  className="rounded-none bg-red-600 hover:bg-red-700 text-white">
                  <Trash2 className="w-4 h-4 mr-2" /> {submitting ? "Scheduling…" : "Request deletion"}
                </Button>
              </div>
            )}

            <p className="text-xs text-slate-500 leading-relaxed">
              Data retained for audit or legal-hold purposes (payment records, policy acceptances) may be preserved for the
              minimum period required by applicable law. See our{" "}
              <a className="underline underline-offset-4 hover:text-slate-900" href="/privacy">Privacy Policy</a>.
            </p>
          </div>
        </section>

        <div className="mt-10 flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-none border-slate-300" data-testid="back-to-dashboard-btn">
            Back to dashboard
          </Button>
          <Button variant="outline" onClick={logout} className="rounded-none border-slate-300" data-testid="logout-btn-account">
            Sign out
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

const TONES = {
  slate: "bg-slate-900",
  amber: "bg-amber-600",
  red: "bg-red-600",
  orange: "bg-orange-600",
};

function RetentionStat({ label, value, hint, bar, testid }) {
  return (
    <div className="border border-slate-200 -mt-px -ml-px p-4 md:p-5 bg-white" data-testid={testid}>
      <div className="label-tiny mb-1">{label}</div>
      <div className="font-serif-display text-2xl md:text-3xl text-slate-900 leading-none">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      {bar && (
        <div className="mt-3 h-1 bg-slate-200 relative overflow-hidden">
          <div
            className={`absolute inset-y-0 left-0 ${TONES[bar.tone] || TONES.slate}`}
            style={{ width: `${Math.max(0, Math.min(100, bar.pct))}%` }}
          />
        </div>
      )}
    </div>
  );
}
